/**
 * The Quarter-at-a-Glance board, redesigned as a narrative quarterly
 * report answering a team member's four questions:
 *   01 How is the business performing?
 *   02 How are we trending?
 *   03 How is my team doing?
 *   04 How busy have we been - and will be?
 *   05 Records & culture
 * Shared by the staff editor (editable=true → pencil overrides) and
 * the gated public page. Figures arrive pre-computed with vs-same-
 * quarter-last-year and vs-previous-quarter comparisons and 8-quarter
 * trends; every figure carries a ⓘ methodology tooltip.
 */
import { createContext, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Pencil, TrendingUp, TrendingDown, Sparkles, Info, Loader2, Minus, Trophy, ArrowRight, FileText, Flag, PenLine, Rocket, Zap } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { InfoTip, InfoTipContext } from "./methodology";
import { MiniBars, TEAM_HEX } from "./QuarterCharts";
import QuarterLifecycle from "./QuarterLifecycle";
import {
  LostReasons,
  WorkMix,
  Milestones,
  useInView,
} from "./QuarterExtras";

const MONEY_KEYS = new Set(["turnover", "avg_proposal_value", "avg_project_value"]);

const TEAM_COLORS = {
  UK: "bg-[#2a9d8f]/10 text-[#1f7468] border-[#2a9d8f]/25",
  International: "bg-[#e9c46a]/15 text-[#8a6d1f] border-[#e9c46a]/40",
  PSE: "bg-[#f4a261]/15 text-[#9c5620] border-[#f4a261]/40",
  PMS: "bg-[#e76f51]/10 text-[#a2442c] border-[#e76f51]/35",
  Other: "bg-stone-100 text-stone-600 border-stone-200",
};

export const fmtMoney = (v) =>
  v == null ? "–" : `£${Math.round(Number(v)).toLocaleString("en-GB")}`;

/* ---------------------------------------------------------- count-up */

function useCountUp(target, start, duration = 900) {
  const [display, setDisplay] = useState(target);
  const done = useRef(false);
  useEffect(() => {
    if (!start || done.current || target == null || typeof target !== "number") {
      setDisplay(target);
      return undefined;
    }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(target);
      done.current = true;
      return undefined;
    }
    done.current = true;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);
  return typeof target === "number" ? display : target;
}

export function CountUp({ value, format }) {
  const [ref, seen] = useInView(0.2);
  const v = useCountUp(value, seen);
  if (value == null) return <>–</>;
  return <span ref={ref}>{format ? format(v) : Math.round(v).toLocaleString("en-GB")}</span>;
}

const fmtFor = (key) => {
  if (MONEY_KEYS.has(key)) return (v) => fmtMoney(v);
  if (key === "win_rate") return (v) => `${Math.round(v)}%`;
  return (v) => Math.round(v).toLocaleString("en-GB");
};

/* ---------------------------------------------------------- badges */

const CmpContext = createContext({});

export function YoyBadge({
  pct, name = "the same quarter last year", invert = false, light = false,
  curValue, prevValue, format, nameSuffix, note,
}) {
  if (pct == null) return null;
  const label = nameSuffix ? `${name} ${nameSuffix}` : name;
  const fmt = format || ((v) => Math.round(v).toLocaleString("en-GB"));
  const detail =
    prevValue != null && curValue != null
      ? `${label}: ${fmt(prevValue)} · now: ${fmt(curValue)}.`
      : null;
  const tail = note ? ` ${note}` : "";
  if (pct === 0) {
    return (
      <span
        title={(detail ? `Level with ${label} - no change. ${detail}` : `Level with ${label} - no change.`) + tail}
        className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full border ${
          light
            ? "text-stone-200 bg-white/10 border-white/25"
            : "text-stone-500 bg-white border-stone-200"
        }`}
      >
        <Minus className="w-2.5 h-2.5" />
        same as {label}
      </span>
    );
  }
  const up = pct > 0;
  const good = invert ? pct < 0 : pct > 0;
  const sentence =
    (detail
      ? `${detail} That's ${Math.abs(pct)}% ${up ? "higher" : "lower"}.`
      : `${Math.abs(pct)}% ${up ? "more" : "less"} than ${label}.`) +
    (invert ? " For this measure, lower is better." : "") + tail;
  const tone = light
    ? good
      ? "text-emerald-300 bg-emerald-400/15 border-emerald-300/40"
      : "text-rose-300 bg-rose-400/15 border-rose-300/40"
    : good
      ? "text-[#2d6a4f] bg-white border-[#2d6a4f]/25"
      : "text-[#9b2226] bg-white border-[#9b2226]/25";
  return (
    <span
      title={sentence}
      className={`inline-flex items-center gap-1 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-full border cursor-help ${tone}`}
    >
      {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
      {up ? "+" : ""}
      {pct}% vs {label}
    </span>
  );
}

function CompareBadges({ metric, invert = false, light = false, format }) {
  const cmp = useContext(CmpContext);
  return (
    <>
      <YoyBadge
        pct={metric?.yoy_pct}
        name={cmp.yoy || "the same quarter last year"}
        invert={invert}
        light={light}
        curValue={metric?.value}
        prevValue={metric?.yoy_value}
        format={format}
        nameSuffix={metric?.cmp_suffix_yoy || metric?.cmp_suffix}
        note={metric?.cmp_note_yoy}
      />
      <YoyBadge
        pct={metric?.qoq_pct}
        name={cmp.qoq || "the previous quarter"}
        invert={invert}
        light={light}
        curValue={metric?.value}
        prevValue={metric?.qoq_value}
        format={format}
        nameSuffix={metric?.cmp_suffix_qoq || metric?.cmp_suffix}
        note={metric?.cmp_note_qoq}
      />
      <YoyBadge
        pct={metric?.avg3_pct}
        name={cmp.avg3 || "3-yr avg"}
        invert={invert}
        light={light}
        curValue={metric?.value}
        prevValue={metric?.avg3_value}
        format={format}
        nameSuffix={metric?.cmp_suffix_avg3 || metric?.cmp_suffix}
        note={metric?.cmp_note_avg3}
      />
    </>
  );
}

function TeamChips({ breakdown }) {
  if (!breakdown) return null;
  const entries = Object.entries(breakdown).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {entries.map(([team, n]) => (
        <span
          key={team}
          className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${TEAM_COLORS[team] || TEAM_COLORS.Other}`}
        >
          {team} {n}
        </span>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------- stat tile */

function StatTile({ id, label, sub, metric, unit, editable, onOverride, invert = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const value = metric?.value;
  return (
    <div
      className="relative rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-5 flex flex-col gap-1 q-card q-reveal"
      data-testid={`qkpi-${id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{label}</p>
          <InfoTip id={id} />
        </div>
        {editable && (
          <button
            type="button"
            className="text-stone-300 hover:text-[#1B4F8A] transition-colors"
            data-testid={`qkpi-edit-${id}`}
            onClick={() => {
              setDraft(value == null ? "" : String(value));
              setEditing((e) => !e);
            }}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-32 border border-stone-300 rounded-lg px-2 py-1 text-sm font-mono"
            data-testid={`qkpi-input-${id}`}
            autoFocus
          />
          <button
            type="button"
            className="text-xs text-white bg-[#1B4F8A] rounded-lg px-2 py-1"
            data-testid={`qkpi-apply-${id}`}
            onClick={() => {
              onOverride(id, draft);
              setEditing(false);
            }}
          >
            Set
          </button>
          <button
            type="button"
            className="text-xs text-stone-400"
            onClick={() => {
              onOverride(id, null);
              setEditing(false);
            }}
          >
            Auto
          </button>
        </div>
      ) : (
        <p className="font-semibold tracking-tight text-3xl text-slate-900 leading-none mt-1">
          <CountUp value={value} format={fmtFor(id)} />
          {unit && value != null && <span className="text-base font-normal text-stone-400"> {unit}</span>}
        </p>
      )}
      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
        <CompareBadges metric={metric} invert={invert} format={fmtFor(id)} />
        {metric?.overridden && (
          <span className="text-[9px] font-mono uppercase text-amber-600" data-testid={`qkpi-overridden-${id}`}>
            edited
          </span>
        )}
      </div>
      {sub && <p className="text-[10px] text-stone-400 leading-snug mt-0.5">{sub}</p>}
      <MiniBars trend={metric?.trend} format={fmtFor(id)} />
      <TeamChips breakdown={metric?.breakdown} />
    </div>
  );
}

/* ---------------------------------------------------------- heroes */

function Takeaways({ tKey, fallback, tw }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const lines = (tw?.data?.[tKey] || []).filter(Boolean);
  const isAction = (l) => /^action\s*:/i.test(l);
  const facts = lines.filter((l) => !isAction(l));
  const actions = lines.filter(isAction);
  const canEdit = Boolean(tw?.editable);
  const busy = tw?.regenerating === tKey || tw?.regenerating === "all";
  if (!tKey || (!canEdit && !lines.length)) return fallback || null;
  return (
    <div className="w-full mt-4" data-testid={`takeaways-${tKey}`}>
      {lines.length > 0 && !editing && (
        <div className="rounded-xl bg-[#f3faf7] border border-[#2a9d8f]/25 px-4 py-3 text-left">
          <p className="text-[9px] font-mono uppercase tracking-widest text-[#1B4F8A] flex items-center gap-1.5">
            <Zap className="w-3 h-3 shrink-0" /> Key takeaways
            {canEdit && (
              <span className="inline-flex items-center gap-2 ml-auto">
                {tw?.generatedAt && (
                  <span
                    className="text-stone-400 normal-case tracking-normal font-sans"
                    title="The board's numbers move as SharePoint updates; regenerate before publishing so the words match the figures."
                    data-testid={`takeaways-stamp-${tKey}`}
                  >
                    AI written{" "}
                    {new Date(tw.generatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{" "}
                    {new Date(tw.generatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                <button
                  type="button"
                  className="text-stone-300 hover:text-[#1B4F8A] transition-colors"
                  title="Edit the takeaways"
                  data-testid={`takeaways-edit-${tKey}`}
                  onClick={() => {
                    setDraft(lines.join("\n"));
                    setEditing(true);
                  }}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  className="text-stone-300 hover:text-[#1f7468] transition-colors disabled:opacity-50"
                  title="Rewrite with AI"
                  data-testid={`takeaways-regen-${tKey}`}
                  disabled={busy}
                  onClick={() => tw.onRegen(tKey)}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                </button>
              </span>
            )}
          </p>
          <ul className="mt-2 space-y-1.5">
            {facts.map((l, i) => (
              <li key={i} className="text-[11px] text-slate-700 leading-snug flex gap-2">
                <span className="w-1 h-1 rounded-full bg-[#2a9d8f] mt-[5px] shrink-0" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
          {actions.length > 0 && (
            <div className="mt-2.5 pt-2 border-t border-[#2a9d8f]/20 space-y-1.5" data-testid={`takeaways-actions-${tKey}`}>
              {actions.map((l, i) => (
                <p key={i} className="text-[11px] font-medium text-[#1B4F8A] leading-snug flex gap-2">
                  <ArrowRight className="w-3 h-3 mt-[2px] shrink-0 text-[#2a9d8f]" />
                  <span>{l.replace(/^action\s*:\s*/i, "")}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && (
        <div className="rounded-xl bg-white border border-[#1B4F8A]/30 px-4 py-3 text-left">
          <p className="text-[9px] font-mono uppercase tracking-widest text-[#1B4F8A]">
            Key takeaways · one per line · start a line with &quot;Action:&quot; for an action point
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={7}
            className="w-full mt-2 border border-stone-200 rounded-lg px-2.5 py-1.5 text-[11px] leading-snug"
            data-testid={`takeaways-input-${tKey}`}
          />
          <div className="flex justify-end gap-3 mt-1.5">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-[10px] text-stone-500 hover:text-slate-900"
              data-testid={`takeaways-cancel-${tKey}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                tw.onSave(tKey, draft.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 7));
                setEditing(false);
              }}
              className="text-[10px] font-medium text-[#1B4F8A] hover:text-[#163f6e]"
              data-testid={`takeaways-save-${tKey}`}
            >
              Apply
            </button>
          </div>
        </div>
      )}
      {!lines.length && !editing && canEdit && (
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => tw.onRegen(tKey)}
            disabled={busy}
            data-testid={`takeaways-generate-${tKey}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1f7468]/40 bg-[#1f7468]/5 px-3 py-1.5 text-[11px] text-[#1f7468] hover:bg-[#1f7468]/10 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Write key takeaways
          </button>
          {fallback}
        </div>
      )}
    </div>
  );
}

function HeroKpi({ title, infoId, overrideKey, metric, format, footnote, editable, onOverride, testid, children, tw, twKey }) {
  const teams = metric?.teams || [];
  return (
    <div
      className={"rounded-2xl bg-[#eef4fa] border border-[#1B4F8A]/10 shadow-sm shadow-[#1B4F8A]/5 text-slate-900 p-6 sm:p-8 relative overflow-hidden q-card q-reveal"}
      data-testid={testid}
    >
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #0f172a 1px, transparent 1px)", backgroundSize: "22px 22px" }}
      />
      <div className="relative flex flex-col items-center text-center">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone-500 flex items-center gap-1.5">
          {title}
          <InfoTip id={infoId} />
          {editable && (
            <button
              type="button"
              className="text-stone-300 hover:text-[#1B4F8A] transition-colors"
              data-testid={`qkpi-edit-${overrideKey}`}
              onClick={() => {
                const v = window.prompt(`Override ${title} (blank = auto):`, metric?.value ?? "");
                if (v !== null) onOverride(overrideKey, v === "" ? null : v);
              }}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </p>
        <p className="font-semibold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-none mt-4">
          <CountUp value={metric?.value} format={format} />
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap mt-4">
          <CompareBadges metric={metric} format={format} />
          {metric?.overridden && (
            <span className="text-[9px] font-mono uppercase text-amber-600">edited</span>
          )}
        </div>
        <div className="w-full max-w-md mt-3 rounded-xl bg-white border border-[#1B4F8A]/10 px-3 pt-2 pb-1">
          <MiniBars trend={metric?.trend} format={format} fluid w={440} h={64} />
        </div>
        {children}
        {teams.length > 0 && (
          <div
            className="w-full flex flex-wrap justify-center gap-x-10 gap-y-4 mt-6 rounded-xl bg-white border border-[#1B4F8A]/10 px-6 py-4"
            data-testid={`${testid}-teams`}
          >
            {teams.map((t) => (
              <div key={t.team} className="text-center" data-testid={`${testid}-team-${t.team}`}>
                <p className="text-[9px] font-mono uppercase tracking-widest text-stone-500">{t.team}</p>
                <p className="text-xl font-semibold tracking-tight mt-1">
                  {t.value == null ? "–" : format(t.value)}
                </p>
                <div className="flex flex-col items-center gap-1 mt-1.5">
                  <CompareBadges metric={t} format={format} />
                </div>
              </div>
            ))}
          </div>
        )}
        <Takeaways
          tKey={twKey}
          tw={tw}
          fallback={<p className="text-[10px] text-stone-400 mt-4">{footnote}</p>}
        />
      </div>
    </div>
  );
}

function TurnoverHero({ kpis, editable, onOverride, tw }) {
  const conc = kpis.turnover?.concentration;
  return (
    <HeroKpi
      title={`${kpis.compare?.this || "This quarter"} Turnover`}
      infoId="turnover"
      overrideKey="turnover"
      metric={kpis.turnover}
      format={fmtMoney}
      footnote="The work we billed clients for this quarter, from the WiP ledger."
      editable={editable}
      onOverride={onOverride}
      testid="qkpi-turnover"
      tw={tw}
      twKey="turnover"
    >
      {conc?.top5_pct != null && (
        <div className="w-full max-w-lg mt-5" data-testid="turnover-concentration">
          <p className="text-[11px] font-mono text-stone-500">
            Our top 5 clients account for <strong className="text-slate-900">{conc.top5_pct}%</strong> of
            this quarter's turnover.
          </p>
          <div className="flex flex-wrap justify-center gap-1.5 mt-2">
            {(conc.top || []).map((c, i) => (
              <span
                key={`${c.client}-${i}`}
                className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-white border border-[#1B4F8A]/10 text-stone-600"
              >
                {c.client} <strong className="text-slate-900">{fmtMoney(c.value)}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
    </HeroKpi>
  );
}

function OpportunitiesHero({ kpis, editable, onOverride, tw }) {
  const biggest = kpis.avg_proposal_value?.biggest || [];
  const topClients = kpis.proposals_submitted?.top_value_clients || [];
  return (
    <HeroKpi
      title={`${kpis.compare?.this || "This quarter"} proposals sent to clients`}
      infoId="proposals_submitted"
      overrideKey="proposals_submitted"
      metric={kpis.proposals_submitted}
      format={(v) => Math.round(v).toLocaleString("en-GB")}
      footnote="Opportunities we quoted this quarter - revised versions of the same proposal count once."
      editable={editable}
      onOverride={onOverride}
      testid="qkpi-proposals_submitted"
      tw={tw}
      twKey="proposals_submitted"
    >
      {biggest.length > 0 && (
        <div className="mt-5" data-testid="quoted-biggest">
          <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
            Biggest quotes this quarter
          </p>
          <div className="flex flex-wrap justify-center gap-1.5 mt-2">
            {biggest.map((b, i) => (
              <span
                key={`${b.client}-${i}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full bg-white border border-[#1B4F8A]/10 text-stone-600"
              >
                <FileText className="w-3 h-3 text-[#1B4F8A]" />
                {b.client} <strong className="text-slate-900">{fmtMoney(b.value)}</strong>
                {b.team && (
                  <span className="text-[9px] uppercase tracking-wide text-white bg-[#1B4F8A]/70 rounded-full px-1.5 py-0.5">
                    {b.team}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
      {topClients.length > 0 && (
        <div className="mt-5" data-testid="quoted-top-clients">
          <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
            Top 5 clients quoted by value
          </p>
          <div className="flex flex-wrap justify-center gap-1.5 mt-2">
            {topClients.map((c) => (
              <span
                key={c.client}
                title={`${c.client}: ${c.quotes} quote${c.quotes === 1 ? "" : "s"} this quarter worth ${fmtMoney(c.value)} in total${c.teams?.length ? ` · quoted by ${c.teams.join(" + ")}` : ""}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full bg-white border border-[#1B4F8A]/10 text-stone-600 cursor-help"
                data-testid={`quoted-top-client-${c.client}`}
              >
                <Trophy className="w-3 h-3 text-[#e9c46a]" />
                {c.client} <strong className="text-slate-900">{fmtMoney(c.value)}</strong>
                <span className="text-[9px] text-stone-400">×{c.quotes}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </HeroKpi>
  );
}

function WinRateHero({ kpis, editable, onOverride, tw }) {
  const wr = kpis.win_rate || {};
  const outcome = kpis.charts?.outcome;
  const mat = wr.maturity;
  const won = wr.won ?? outcome?.won ?? kpis.submitted_commissioned?.value;
  const open = wr.undecided?.count ?? outcome?.open;
  const lost = wr.lost ?? outcome?.lost;
  const med = wr.medilab;
  const decided = (won || 0) + (lost || 0);
  const decidedRate = decided > 0 ? Math.round((100 * (won || 0)) / decided) : null;
  const total = (won || 0) + (open || 0) + (lost || 0);
  const segs = [
    { id: "won", label: "Won", n: won || 0, hex: "#2a9d8f" },
    { id: "open", label: "Still being decided", n: open || 0, hex: "#e9c46a" },
    { id: "lost", label: "Lost", n: lost || 0, hex: "#9b2226" },
  ];
  return (
    <HeroKpi
      title={`${kpis.compare?.this || "This quarter"} Win Rate`}
      infoId="win_rate"
      overrideKey="win_rate"
      metric={kpis.win_rate}
      format={(v) => `${Math.round(v)}%`}
      footnote={
        open != null && total > 0
          ? `${open} of the ${total} research-team quotes are still with clients, so this figure keeps rising as decisions land. To keep the race fair, the badges compare against what each quarter had converted at this same point in its life - hover a badge for where it eventually finished.${med?.value != null ? " MediLAB is reported separately below." : ""}`
          : "Share of the quarter's quotes won so far - keeps rising as open quotes convert."
      }
      editable={editable}
      onOverride={onOverride}
      testid="qkpi-win_rate"
      tw={tw}
      twKey="win_rate"
    >
      {total > 0 && (
        <div className="w-full max-w-lg mt-5" data-testid="win-rate-outcome">
          <div className="flex h-2.5 rounded-full overflow-hidden">
            {segs.filter((s) => s.n > 0).map((s) => (
              <div
                key={s.id}
                style={{ width: `${(100 * s.n) / total}%`, backgroundColor: s.hex }}
                title={`${s.label}: ${s.n}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-2.5">
            {segs.map((s) => (
              <span key={s.id} className="text-[11px] font-mono text-stone-500 inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.hex }} />
                {s.label}: <strong className="text-slate-900">{s.n}</strong>
              </span>
            ))}
          </div>
          {open > 0 && mat?.gain_min != null && (
            <p
              className="text-sm font-mono text-[#1B4F8A] mt-3 cursor-help"
              data-testid="win-rate-maturity"
              title={(mat.history || [])
                .map((h) => `${h.label}: ${h.at_stage}% at this stage → ${h.final}% final`)
                .join(" · ")}
            >
              Still converting: {open} quotes remain open. The last {mat.history.length} quarters
              climbed another{" "}
              <strong>
                {mat.gain_min === mat.gain_max
                  ? `${mat.gain_min}`
                  : `${mat.gain_min} to ${mat.gain_max}`}{" "}
                points
              </strong>{" "}
              after this stage.
            </p>
          )}
          {decidedRate != null && (
            <p className="text-sm font-mono text-[#9c5620] mt-2" data-testid="win-rate-decided">
              Counting only the {decided} quotes already decided, the win rate is{" "}
              <strong>{decidedRate}%</strong>.
            </p>
          )}
          {med?.value != null && (
            <div
              className="mt-4 rounded-xl border border-[#6d597a]/25 bg-[#6d597a]/5 p-3.5 text-left"
              data-testid="win-rate-medilab"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-[9px] font-mono uppercase tracking-widest text-[#6d597a]">
                  MediLAB · reported separately
                </span>
                <span className="font-semibold text-slate-900 text-lg leading-none">{med.value}%</span>
                <span className="text-[11px] font-mono text-stone-500">
                  {med.won} won · {med.lost} lost{med.open > 0 ? <> · {med.open} still open</> : null}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                <CompareBadges metric={med} format={(v) => `${Math.round(v)}%`} />
              </div>
              <p className="text-[10px] text-stone-500 mt-1.5 leading-snug">
                MediLAB orders normally convert the day they are quoted and the order form has no
                lost status, so a quote not taken up within {med.expiry_days || 60} days is counted
                as lost. Kept separate so it does not flatter the research-team rate.
              </p>
            </div>
          )}
        </div>
      )}
    </HeroKpi>
  );
}

function ClientsQuotedHero({ kpis, editable, onOverride, tw }) {
  const m = kpis.unique_clients;
  const brandNew = m?.new_count;
  const returning = m?.returning_count;
  const busiest = m?.busiest || [];
  const splitTotal = (brandNew || 0) + (returning || 0);
  return (
    <HeroKpi
      title={`${kpis.compare?.this || "This quarter"} Clients We Quoted`}
      infoId="unique_clients"
      overrideKey="unique_clients"
      metric={m}
      format={(v) => Math.round(v).toLocaleString("en-GB")}
      footnote="Unique client companies we sent at least one quote to this quarter. A client quoted by two teams counts once overall but appears in both team figures."
      editable={editable}
      onOverride={onOverride}
      testid="qkpi-unique_clients"
      tw={tw}
      twKey="unique_clients"
    >
      {splitTotal > 0 && (
        <div className="w-full max-w-lg mt-5" data-testid="clients-new-returning">
          <div className="flex h-2.5 rounded-full overflow-hidden">
            {brandNew > 0 && (
              <div style={{ width: `${(100 * brandNew) / splitTotal}%`, backgroundColor: "#2a9d8f" }} title={`Brand-new: ${brandNew}`} />
            )}
            {returning > 0 && (
              <div style={{ width: `${(100 * returning) / splitTotal}%`, backgroundColor: "#5b7aa0" }} title={`Returning: ${returning}`} />
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-2.5">
            <span className="text-[11px] font-mono text-stone-500 inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: "#2a9d8f" }} />
              Brand-new to us: <strong className="text-slate-900">{brandNew}</strong>
            </span>
            <span className="text-[11px] font-mono text-stone-500 inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: "#5b7aa0" }} />
              Returning: <strong className="text-slate-900">{returning}</strong>
            </span>
          </div>
          {busiest.length > 0 && (
            <div className="mt-4" data-testid="clients-busiest">
              <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
                Busiest clients this quarter
              </p>
              <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                {busiest.map((b) => (
                  <span
                    key={b.client}
                    className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-white border border-[#1B4F8A]/10 text-stone-600"
                  >
                    {b.client} <strong className="text-slate-900">×{b.quotes}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </HeroKpi>
  );
}

function AvgValueHero({ kpis, editable, onOverride, tw }) {
  const m = kpis.avg_proposal_value;
  return (
    <HeroKpi
      title={`${kpis.compare?.this || "This quarter"} Average Quote Value`}
      infoId="avg_proposal_value"
      overrideKey="avg_proposal_value"
      metric={m}
      format={fmtMoney}
      footnote="Average value of the quarter's opportunities, each counted once (revisions deduped), from the quote's turnover figure."
      editable={editable}
      onOverride={onOverride}
      testid="qkpi-avg_proposal_value"
      tw={tw}
      twKey="avg_proposal_value"
    />
  );
}

function WonHero({ kpis, editable, onOverride, tw }) {
  const m = kpis.projects_commissioned;
  const won = kpis.value_won;
  const avg = kpis.avg_project_value;
  const biggest = won?.biggest || [];
  return (
    <HeroKpi
      title={`${kpis.compare?.this || "This quarter"} Projects Commissioned`}
      infoId="projects_commissioned"
      overrideKey="projects_commissioned"
      metric={m}
      format={(v) => Math.round(v).toLocaleString("en-GB")}
      footnote="Everything commissioned this quarter, whenever it was quoted - multiple versions of the same proposal count once. Team splits use each project's Team in charge."
      editable={editable}
      onOverride={onOverride}
      testid="qkpi-projects_commissioned"
      tw={tw}
      twKey="projects_commissioned"
    >
      <div className="w-full max-w-lg mt-5" data-testid="won-extras">
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
          {avg?.value != null && (
            <div className="text-center" data-testid="won-avg">
              <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
                Average project value
              </p>
              <p className="text-2xl font-semibold tracking-tight mt-1">{fmtMoney(avg.value)}</p>
              <div className="flex flex-col items-center gap-1 mt-1.5">
                <CompareBadges metric={avg} format={fmtMoney} />
              </div>
            </div>
          )}
        </div>
        {biggest.length > 0 && (
          <div className="mt-5" data-testid="won-biggest">
            <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
              Biggest wins this quarter
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-2">
              {biggest.map((b, i) => (
                <span
                  key={`${b.client}-${i}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full bg-white border border-[#1B4F8A]/10 text-stone-600"
                >
                  <Trophy className="w-3 h-3 text-[#e9c46a]" />
                  {b.client} <strong className="text-slate-900">{fmtMoney(b.value)}</strong>
                  {b.team && (
                    <span className="text-[9px] uppercase tracking-wide text-white bg-[#1B4F8A]/70 rounded-full px-1.5 py-0.5">
                      {b.team}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
        {(won?.cancelled?.count || 0) > 0 && (
          <p className="text-[11px] font-mono text-[#9b2226] mt-4" data-testid="won-cancelled">
            {won.cancelled.count} of this quarter&apos;s wins {won.cancelled.count === 1 ? "was" : "were"} later
            cancelled - <strong>{fmtMoney(won.cancelled.value)}</strong> walked away.
          </p>
        )}
      </div>
    </HeroKpi>
  );
}

/* ---------------------------------------------------------- speed journey */

const CORE_SPEED_TEAMS = ["UK", "International", "PSE", "PMS"];

function SpeedLanes({ metric, unitWord, testid }) {
  const rows = (metric?.teams || []).filter(
    (t) => t.value != null || CORE_SPEED_TEAMS.includes(t.team),
  );
  const timed = rows.filter((t) => t.value != null);
  if (!timed.length) return null;
  const max = Math.max(...timed.map((t) => t.value), 1);
  const fastest = Math.min(...timed.map((t) => t.value));
  return (
    <div className="flex flex-col gap-1.5 mt-4" data-testid={testid}>
      <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
        The race by team · shorter bar = faster
      </p>
      {rows.map((t) =>
        t.value == null ? (
          <div
            key={t.team}
            className="flex items-center gap-2 opacity-70"
            title={`${t.team}: no timed ${unitWord} this quarter${
              t.yoy_value != null ? ` · was ${t.yoy_value} days the same quarter last year` : ""
            }`}
          >
            <span className="w-20 shrink-0 text-left text-[10px] font-mono text-stone-400 truncate">
              {t.team}
            </span>
            <div className="flex-1 h-3 bg-stone-100 rounded-full" />
            <span className="w-16 shrink-0 text-right text-[10px] font-mono text-stone-400">–</span>
          </div>
        ) : (
          <div
            key={t.team}
            className="flex items-center gap-2"
            title={`${t.team}: ${t.value} days this quarter (median of ${t.n} ${unitWord})${
              t.yoy_value != null ? ` · was ${t.yoy_value} days the same quarter last year` : ""
            }${t.qoq_value != null ? ` · ${t.qoq_value} days the previous quarter` : ""}`}
          >
            <span className="w-20 shrink-0 text-left text-[10px] font-mono text-stone-500 truncate">
              {t.team}
            </span>
            <div className="flex-1 h-3 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max((t.value / max) * 100, 6)}%`,
                  backgroundColor: TEAM_HEX[t.team] || "#a8a29e",
                }}
              />
            </div>
            <span className="w-16 shrink-0 text-right text-[10px] font-mono text-stone-500 whitespace-nowrap">
              <strong className="text-slate-900">{t.value}d</strong>
              <span className="text-stone-400"> · {t.n}</span>
              {t.value === fastest && timed.length > 1 && (
                <Zap className="w-2.5 h-2.5 inline ml-1 -mt-0.5 text-[#d6a419] fill-[#d6a419]" />
              )}
            </span>
          </div>
        ),
      )}
    </div>
  );
}

function SpeedSegment({ id, from, to, FromIcon, ToIcon, metric, sub, unitWord, editable, onOverride }) {
  return (
    <div
      className="rounded-xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-5 flex flex-col"
      data-testid={`qkpi-${id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="w-6 h-6 shrink-0 rounded-full bg-[#152b45] text-white flex items-center justify-center">
          <FromIcon className="w-3 h-3" />
        </span>
        <span className="text-[9px] font-mono uppercase text-stone-500 whitespace-nowrap min-w-0 truncate">
          {from}
        </span>
        <div className="flex-1 h-[2px] min-w-2 q-flow" />
        <ArrowRight className="w-3.5 h-3.5 shrink-0 text-[#2a9d8f]" />
        <span className="text-[9px] font-mono uppercase text-stone-500 whitespace-nowrap min-w-0 truncate">
          {to}
        </span>
        <span className="w-6 h-6 shrink-0 rounded-full bg-[#2a9d8f] text-white flex items-center justify-center">
          <ToIcon className="w-3 h-3" />
        </span>
      </div>
      <div className="flex items-end justify-center gap-2 mt-4">
        <p className="font-semibold tracking-tight text-5xl text-slate-900 leading-none">
          <CountUp value={metric?.value} />
        </p>
        <span className="text-base text-stone-400 pb-0.5">days</span>
        <span className="pb-1 flex items-center gap-1">
          <InfoTip id={id} />
          {editable && (
            <button
              type="button"
              className="text-stone-300 hover:text-[#1B4F8A] transition-colors"
              data-testid={`qkpi-edit-${id}`}
              onClick={() => {
                const v = window.prompt(`Override ${from} → ${to} days (blank = auto):`, metric?.value ?? "");
                if (v !== null) onOverride(id, v === "" ? null : v);
              }}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </span>
      </div>
      <div className="flex items-center justify-center gap-1.5 flex-wrap mt-2.5">
        <CompareBadges metric={metric} invert format={(v) => `${Math.round(v)} days`} />
        {metric?.hist?.pct != null && (
          <YoyBadge
            pct={metric.hist.pct}
            name="since-2023 norm"
            invert
            curValue={metric.value}
            prevValue={metric.hist.value}
            format={(v) => `${Math.round(v)} days`}
            note={`The norm is the median across ${metric.hist.n} projects since Jan 2023.`}
          />
        )}
        {metric?.overridden && (
          <span className="text-[9px] font-mono uppercase text-amber-600" data-testid={`qkpi-overridden-${id}`}>
            edited
          </span>
        )}
      </div>
      <p className="text-[10px] text-stone-400 text-center mt-1.5">{sub}</p>
      <SpeedLanes metric={metric} unitWord={unitWord} testid={`speed-teams-${id}`} />
      <div className="mt-auto pt-3 flex justify-center">
        <div className="w-full max-w-[260px] min-w-0">
          <MiniBars trend={metric?.trend} format={(v) => `${Math.round(v)} days`} w={220} h={40} fluid />
        </div>
      </div>
    </div>
  );
}

function JourneyRibbon({ journey }) {
  const steps = (journey?.steps || []).filter((s) => s.days != null);
  if (!steps.length) return null;
  const nodes = [steps[0].from, ...steps.map((s) => s.to)];
  return (
    <div
      className="mt-6 rounded-xl border border-[#1B4F8A]/10 bg-white/70 p-4"
      data-testid="speed-journey-ribbon"
    >
      <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400 flex items-center justify-center gap-1">
        The whole journey · typical days at each step · a guide, not a stopwatch
        <InfoTip id="journey" />
      </p>
      <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-1">
        {nodes.map((label, i) => (
          <Fragment key={label}>
            <div className="flex flex-col items-center shrink-0 w-20">
              <span
                className={`w-3 h-3 rounded-full ${
                  i === 0 ? "bg-[#152b45]" : i === nodes.length - 1 ? "bg-[#2d6a4f]" : "bg-[#2a9d8f]"
                }`}
              />
              <span className="text-[9px] font-mono uppercase text-stone-500 mt-1.5 text-center leading-tight">
                {label}
              </span>
            </div>
            {i < steps.length && (
              <div
                className="flex-1 min-w-14 flex flex-col items-center cursor-help"
                title={`${steps[i].from} to ${steps[i].to}: typically ${steps[i].days} days`}
                data-testid={`journey-step-${steps[i].id}`}
              >
                <span className="text-[11px] font-mono text-slate-900 font-semibold whitespace-nowrap">
                  {steps[i].days}d
                </span>
                <div className="w-full h-[2px] bg-gradient-to-r from-[#2a9d8f]/30 via-[#2a9d8f] to-[#2a9d8f]/30 rounded-full mt-1" />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function SpeedJourney({ kpis, editable, onOverride, tw }) {
  const a = kpis.prop_to_com_days;
  const b = kpis.sow_to_field_days;
  const c = kpis.field_to_finish_days;
  const d = kpis.medilab_turnaround;
  if (a?.value == null && b?.value == null && c?.value == null) return null;
  const segs = [a, b, c];
  const total = segs.every((m) => m?.value != null) ? a.value + b.value + c.value : null;
  const showMedilab = d?.value != null;
  return (
    <div
      className="rounded-2xl border border-[#1B4F8A]/10 bg-gradient-to-br from-white to-stone-50 shadow-sm shadow-[#1B4F8A]/5 p-6 q-card q-reveal"
      data-testid="qkpi-speed"
    >
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
        How fast work moves · median days this quarter
      </p>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4"
      >
        <SpeedSegment
          id="prop_to_com_days"
          from="Quote"
          to="Won"
          FromIcon={FileText}
          ToIcon={Trophy}
          metric={a}
          sub={`First quote to the client saying yes${a?.n ? ` - based on ${a.n} wins` : ""}. Fewer days = better.`}
          unitWord="wins"
          editable={editable}
          onOverride={onOverride}
        />
        <SpeedSegment
          id="sow_to_field_days"
          from="Contract"
          to="In field"
          FromIcon={PenLine}
          ToIcon={Rocket}
          metric={b}
          sub={`Signature to fieldwork starting${b?.n ? ` - based on ${b.n} projects` : ""}. Fewer days = better.`}
          unitWord="projects"
          editable={editable}
          onOverride={onOverride}
        />
        <SpeedSegment
          id="field_to_finish_days"
          from="In field"
          to="Finished"
          FromIcon={Rocket}
          ToIcon={Flag}
          metric={c}
          sub={`Fieldwork starting to fieldwork done${c?.n ? ` - based on ${c.n} projects` : ""}. Fewer days = quicker turnaround.`}
          unitWord="projects"
          editable={editable}
          onOverride={onOverride}
        />
        {showMedilab && (
          <SpeedSegment
            id="medilab_turnaround"
            from="Order"
            to="Delivered"
            FromIcon={FileText}
            ToIcon={Flag}
            metric={d}
            sub={`MediLAB runs on a different clock: orders convert the day they are quoted, so we track order to delivery instead${d?.n ? ` - based on ${d.n} orders` : ""}.`}
            unitWord="orders"
            editable={editable}
            onOverride={onOverride}
          />
        )}
      </div>
      <JourneyRibbon journey={kpis.journey} />
      <Takeaways
        tKey="speed"
        tw={tw}
        fallback={
          total != null ? (
            <p className="text-[11px] text-stone-500 text-center mt-4" data-testid="speed-total">
              End to end, a typical win goes from first quote to finished fieldwork in about{" "}
              <strong className="text-slate-900">{total} days</strong>
              <span className="text-stone-400"> (three medians from different project groups - a guide, not a stopwatch).</span>
            </p>
          ) : null
        }
      />
    </div>
  );
}

/* ---------------------------------------------------------- waffle */

function RecommendWaffle({ rec, editable, onOverride, footer }) {
  const definitely = rec?.definitely || 0;
  const might = rec?.might || 0;
  const nope = rec?.not || 0;
  const total = definitely + might + nope;
  const cells = [
    { id: "recommend_definitely", label: "I definitely will", n: definitely, hex: "#2a9d8f", txt: "text-[#1f7468]" },
    { id: "recommend_might", label: "I might do", n: might, hex: "#d4a373", txt: "text-[#8a6d1f]" },
    { id: "recommend_not", label: "I definitely will not", n: nope, hex: "#9b2226", txt: "text-[#9b2226]" },
  ];
  const squares = cells.flatMap((c) =>
    Array.from({ length: Math.min(c.n, 200) }, (_, i) => ({ key: `${c.id}-${i}`, hex: c.hex })),
  );
  return (
    <div className="rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-6 q-card q-reveal" data-testid="qkpi-recommend">
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        Would clients recommend us? · end-of-project survey
        <InfoTip id="recommend" />
      </p>
      {total > 0 ? (
        <>
          <div className="flex flex-wrap gap-1 mt-3" aria-label="one square per client">
            {squares.map((s, i) => (
              <span
                key={s.key}
                className="w-3.5 h-3.5 rounded-[3px] q-pop"
                style={{ backgroundColor: s.hex, animationDelay: `${Math.min(i * 22, 1400)}ms` }}
              />
            ))}
          </div>
          <p className="text-[10px] text-stone-400 mt-2">One square = one client this quarter.</p>
        </>
      ) : (
        <p className="text-xs text-stone-400 mt-2">No survey responses recorded this quarter.</p>
      )}
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
        {cells.map((c) => (
          <span key={c.id} className={`text-xs font-mono ${c.txt} inline-flex items-center gap-1.5`}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.hex }} />
            {c.label}: <strong>{c.n}</strong>
            {editable && (
              <input
                type="number"
                className="w-14 border border-stone-200 rounded px-1 py-0.5 text-[10px]"
                placeholder="edit"
                data-testid={`qkpi-input-${c.id}`}
                onBlur={(e) => e.target.value !== "" && onOverride(c.id, e.target.value)}
              />
            )}
          </span>
        ))}
      </div>
      {footer}
    </div>
  );
}

/* ---------------------------------------------------------- spotlight + cloud */

const CONFETTI_COLOURS = ["#2a9d8f", "#e9c46a", "#d4a373", "#9b2226", "#1B4F8A", "#7fd1c7"];

function NewClients({ data, doors, footer }) {
  const won = data?.names || [];
  const opened = (doors || []).filter(
    (d) => !won.some((w) => w.name.toLowerCase() === d.name.toLowerCase()),
  );
  if ((data?.count || 0) === 0 && opened.length === 0) return null;
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#e9c46a]/60 bg-gradient-to-br from-[#fdf8ec] to-[#f5e9cc] shadow-sm p-6 sm:p-8 q-reveal"
      data-testid="qkpi-new-clients"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="q-confetti"
            style={{
              left: `${(i * 53 + 7) % 100}%`,
              backgroundColor: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
              width: i % 3 ? 6 : 9,
              height: i % 2 ? 11 : 7,
              animationDelay: `${(i % 9) * 0.2}s`,
            }}
          />
        ))}
      </div>
      <div className="relative text-center">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#8a6d1d] flex items-center justify-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          New clients won this quarter
          <InfoTip id="new_clients" />
        </p>
        <p className="font-semibold tracking-tight text-5xl sm:text-6xl leading-none mt-3 text-slate-900">
          <CountUp value={data?.count || 0} />
        </p>
        {won.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2.5 mt-5">
            {won.map((c) => (
              <span
                key={c.name}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-[#e9c46a] shadow-sm px-4 py-2"
                data-testid={`new-client-won-${c.name}`}
              >
                <Trophy className="w-4 h-4 text-[#b98a1f]" />
                <span className="font-serif text-base text-slate-900">{c.name}</span>
                {c.value > 0 && (
                  <span className="text-[11px] font-mono text-stone-500">{fmtMoney(c.value)}</span>
                )}
                {(c.teams || []).map((t) => (
                  <span
                    key={t}
                    className="text-[9px] font-mono uppercase tracking-wider bg-[#152b45] text-white rounded-full px-2 py-0.5"
                  >
                    {t}
                  </span>
                ))}
              </span>
            ))}
          </div>
        )}
        {opened.length > 0 && (
          <div className="mt-6 pt-5 border-t border-[#e9c46a]/40" data-testid="new-doors-opened">
            <p className="text-[9px] font-mono uppercase tracking-widest text-[#8a6d1d]/90">
              New doors opened · first-ever quotes sent to {opened.length}{" "}
              {opened.length === 1 ? "company" : "companies"}
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-2.5">
              {opened.map((d) => (
                <span
                  key={d.name}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full bg-[#2a9d8f]/10 border border-[#2a9d8f]/30 text-[#1d6a60]"
                >
                  {d.name}
                  {(d.teams || []).length > 0 && (
                    <span className="text-[9px] uppercase tracking-wider text-[#2a9d8f]">
                      {d.teams.join(" · ")}
                    </span>
                  )}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-stone-500 mt-2.5">
              Tomorrow's wins brewing - companies we quoted for the very first time this quarter.
            </p>
          </div>
        )}
        {footer}
      </div>
    </div>
  );
}

const CLOUD_COLORS = ["text-slate-900", "text-[#1B4F8A]", "text-[#1f7468]", "text-[#9c5620]", "text-slate-500"];

function Cloud({ rows, hover, seen, baseDelay }) {
  const max = Math.max(...rows.map((t) => t.n));
  const shuffled = [];
  const sorted = [...rows];
  while (sorted.length) {
    shuffled.push(sorted.shift());
    if (sorted.length) shuffled.push(sorted.pop());
  }
  return (
    <p className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1.5 mt-3 text-center leading-relaxed">
      {shuffled.map((t, i) => {
        const scale = max > 1 ? (t.n - 1) / (max - 1) : 0;
        const px = Math.round(12 + scale * 16);
        return (
          <Tooltip key={t.text}>
            <TooltipTrigger asChild>
              <span
                className={scale > 0.6 ? "q-word q-word-big" : "q-word"}
                style={{
                  animationDelay: `${baseDelay + i * 45}ms`,
                  animationPlayState: seen ? "running" : "paused",
                }}
              >
                <span
                  className="q-drift"
                  style={{ "--qd": `${5 + (i % 5)}s`, "--qoff": `${-((i % 7) * 0.9)}s` }}
                >
                  <span
                    className={`q-wordhover font-serif cursor-default ${CLOUD_COLORS[i % CLOUD_COLORS.length]}`}
                    style={{ fontSize: `${px}px`, opacity: 0.55 + scale * 0.45 }}
                    data-testid="topic-word"
                  >
                    {t.text}
                  </span>
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">{hover(t)}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </p>
  );
}

function TopicsCloud({ topics, footer }) {
  const [ref, seen] = useInView();
  if (!topics || Array.isArray(topics)) return null;
  const { quoted = [], quoted_total } = topics;
  if (!quoted.length) return null;
  return (
    <div
      ref={ref}
      className="rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-6 sm:p-8 q-card q-reveal"
      data-testid="qkpi-topics"
    >
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        What&apos;s hot this quarter
        <InfoTip id="topics" />
      </p>
      <div className="mt-3 max-w-3xl mx-auto" data-testid="topics-quoted">
        <p className="text-[9px] font-mono uppercase tracking-widest text-stone-400 text-center">
          What we quoted{quoted_total ? ` · top ${quoted.length} of ${quoted_total} topics` : ""}
        </p>
        <Cloud
          rows={quoted}
          seen={seen}
          baseDelay={0}
          hover={(t) => (
            <>
              <strong>{t.text}</strong> · {t.n} quote{t.n === 1 ? "" : "s"} this quarter
            </>
          )}
        />
      </div>
      <p className="text-[10px] text-stone-400 text-center mt-5" data-testid="topics-footnote">
        Bigger text = more projects; hover a topic for its quote count.
      </p>
      {footer}
    </div>
  );
}

/* ---------------------------------------------------------- scaffolding */

function SectionHeader({ n, title, sub }) {
  return (
    <div className="mb-5 q-reveal">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.3em]" style={{ color: "var(--q-num, #1B4F8A)" }}>{n}</span>
        <span className="h-px flex-1 q-linegrow" style={{ backgroundColor: "var(--q-line, rgba(27,79,138,0.12))" }} />
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2" style={{ color: "var(--q-heading, #152b45)" }}>{title}</h2>
      {sub && <p className="text-sm mt-1 max-w-2xl leading-relaxed" style={{ color: "var(--q-sub, #78716c)" }}>{sub}</p>}
    </div>
  );
}

const fmtQ = (k) => (k ? `Q${k.slice(-1)} ${k.slice(0, 4)}` : "");

function Legend({ compare, baseline }) {
  return (
    <div
      className="rounded-2xl border border-[#1B4F8A]/15 bg-[#eef4fa] px-5 py-4 q-reveal"
      data-testid="qkpi-legend"
    >
      <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#1B4F8A] flex items-center gap-1.5">
        {useContext(InfoTipContext) && <Info className="w-3 h-3 shrink-0" />}
        How to read this page
      </p>
      <ul className="mt-2 flex flex-col gap-1.5 text-[11px] leading-snug text-slate-600">
        <li>
          Each figure is compared with three reference points:{" "}
          <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full border text-[#2d6a4f] bg-[#2d6a4f]/10 border-[#2d6a4f]/20 align-middle">
            <TrendingUp className="w-2.5 h-2.5" />vs {compare?.yoy || "same quarter last year"}
          </span>{" "}
          means <strong>the same three months last year</strong> (the fairest like-for-like, since our
          year has seasonal rhythms),{" "}
          <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full border text-[#2d6a4f] bg-[#2d6a4f]/10 border-[#2d6a4f]/20 align-middle">
            <TrendingUp className="w-2.5 h-2.5" />vs {compare?.qoq || "previous quarter"}
          </span>{" "}
          means <strong>the quarter just gone</strong> (the direction of travel)
          {compare?.avg3 ? (
            <>
              , and{" "}
              <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full border text-[#2d6a4f] bg-[#2d6a4f]/10 border-[#2d6a4f]/20 align-middle">
                <TrendingUp className="w-2.5 h-2.5" />vs {compare.avg3}
              </span>{" "}
              means <strong>the average of this same quarter across the last three years</strong> (the
              long-run norm, less swayed by one unusual year).
            </>
          ) : (
            "."
          )}
        </li>
        {baseline?.mode === "issued" && (
          <li data-testid="qkpi-baseline-note">
            Comparisons are made against <strong>the figures as issued to the team</strong> for{" "}
            {baseline.issued.map(fmtQ).join(", ")}
            {baseline.live?.length ? (
              <>; {baseline.live.map(fmtQ).join(", ")} {baseline.live.length === 1 ? "was" : "were"} never issued, so {baseline.live.length === 1 ? "it is" : "they are"} recomputed from today's data</>
            ) : null}
            .
          </li>
        )}
        <li>
          <span className="text-[#2d6a4f] font-medium">Green = moving in a good direction</span>,{" "}
          <span className="text-[#9b2226] font-medium">red = the opposite</span>. For speed measures
          (anything in days), <em>fewer</em> days is the good direction. Hover any badge for a plain-English
          reading.
        </li>
        <li>
          The small trend line under each figure shows the <strong>last 8 quarters</strong> - the{" "}
          <span className="text-[#1B4F8A] font-medium">blue dot</span> is this quarter, the{" "}
          <span className="text-stone-500 font-medium">hollow dot</span> the same quarter last year. Hover
          the line for each quarter&apos;s value.
        </li>
        {useContext(InfoTipContext) && (
          <li data-testid="qkpi-methodology-hint">
            Hover the <Info className="w-3 h-3 inline align-[-2px] text-stone-400" /> next to any figure to
            see exactly where the number comes from and how it&apos;s calculated.
          </li>
        )}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------- board */

export default function QuarterKpis({
  kpis,
  editable = false,
  onOverride = () => {},
  takeaways = {},
  takeawaysAt = null,
  onSaveTakeaway = () => {},
  onRegenTakeaway = () => {},
  regenerating = null,
}) {
  const tw = {
    data: takeaways,
    generatedAt: takeawaysAt,
    editable,
    onSave: onSaveTakeaway,
    onRegen: onRegenTakeaway,
    regenerating,
  };
  useEffect(() => {
    const els = document.querySelectorAll(".q-reveal, .q-pop, .q-linegrow");
    const run = (el) => {
      el.style.animationPlayState = "running";
      el.querySelectorAll(".q-confetti").forEach((c) => {
        c.style.animationPlayState = "running";
      });
    };
    if (!("IntersectionObserver" in window)) {
      els.forEach(run);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            run(en.target);
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [kpis]);
  if (!kpis) return null;
  const quoted = kpis.proposals_submitted?.value;
  const won = kpis.submitted_commissioned?.value;
  return (
    <TooltipProvider delayDuration={150}>
    <InfoTipContext.Provider value={editable}>
    <CmpContext.Provider value={kpis.compare || {}}>
    <div className="flex flex-col gap-16" data-testid="quarter-kpis">
      <style>{`
        @keyframes qfadeup { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        @keyframes qpop { from { opacity: 0; transform: scale(0.3); } to { opacity: 1; transform: scale(1); } }
        .q-reveal { animation: qfadeup .6s ease-out both; animation-play-state: paused; }
        .q-pop { animation: qpop .35s ease-out both; animation-play-state: paused; }
        .q-linegrow { transform-origin: left; animation: qgrowx .9s cubic-bezier(.2,.8,.3,1) both; animation-play-state: paused; }
        @keyframes qgrowx { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .q-card { transition: transform .2s ease, box-shadow .2s ease; }
        .q-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(21,43,69,0.08); }
        @media (prefers-reduced-motion: reduce) { .q-reveal, .q-pop, .q-linegrow { animation: none; } .q-card, .q-card:hover { transform: none; } }
        .q-confetti { position: absolute; top: -14px; border-radius: 2px; opacity: 0; animation: qconfetti 3.4s ease-in infinite; animation-play-state: paused; }
        @keyframes qconfetti { 0% { transform: translateY(0) rotate(0deg); opacity: 0; } 8% { opacity: .9; } 100% { transform: translateY(380px) rotate(540deg); opacity: 0; } }
        @media (prefers-reduced-motion: reduce) { .q-confetti { animation: none; } }
        .q-flow { background-image: repeating-linear-gradient(90deg, #94a3b8 0 6px, transparent 6px 12px); animation: qflow 1s linear infinite; }
        @keyframes qflow { to { background-position-x: 12px; } }
        @media (prefers-reduced-motion: reduce) { .q-flow { animation: none; } }
        .q-grow { transform-origin: left; animation: qgrow .7s cubic-bezier(.2,.8,.3,1) both; }
        @keyframes qgrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .q-tickin { animation: qtick .4s ease-out both; }
        @keyframes qtick { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .q-grow, .q-tickin { animation: none; } }
        .q-rise { animation: qrise .6s cubic-bezier(.2,.8,.3,1) both; }
        @keyframes qrise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .q-conn { background-image: repeating-linear-gradient(180deg, #94a3b8 0 6px, transparent 6px 12px); transform-origin: top; animation: qflowv 1s linear infinite, qdrop .5s ease-out both; }
        @keyframes qflowv { to { background-position-y: 12px; } }
        @keyframes qdrop { from { transform: scaleY(0); } to { transform: scaleY(1); } }
        @media (prefers-reduced-motion: reduce) { .q-rise, .q-conn { animation: none; } }
        .q-word { display: inline-block; animation: qwordin .5s cubic-bezier(.2,.8,.3,1) both; }
        .q-word-big { display: inline-block; animation: qwordin .5s cubic-bezier(.2,.8,.3,1) both, qglow 2s ease-in-out both; }
        @keyframes qwordin { from { opacity: 0; transform: translateY(10px) scale(.7); } to { opacity: 1; transform: none; } }
        @keyframes qglow { 0%, 45%, 100% { filter: brightness(1); } 65% { filter: brightness(1.7); } }
        .q-drift { display: inline-block; animation: qdrift var(--qd, 6s) ease-in-out var(--qoff, 0s) infinite alternate; }
        @keyframes qdrift { from { transform: translate(1px, -2px); } to { transform: translate(-1px, 2px); } }
        .q-wordhover { display: inline-block; transition: transform .18s ease, opacity .18s ease; }
        .q-wordhover:hover { transform: scale(1.18); opacity: 1 !important; }
        @media (prefers-reduced-motion: reduce) { .q-word, .q-word-big, .q-drift { animation: none; } }
      `}</style>

      <Legend compare={kpis.compare} baseline={kpis.baseline} />

      {/* 01 ------------------------------------------------ performance */}
      <section>
        <SectionHeader
          n="01"
          title="How is the business performing?"
          sub="The headline numbers: the turnover we billed, how much we quoted, how those quotes are converting, and the clients behind it all."
        />
        <div className="grid grid-cols-1 gap-4">
          <TurnoverHero kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
          <OpportunitiesHero kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
          <WinRateHero kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
          <LostReasons data={kpis.lost_reasons} footer={<Takeaways tKey="lost_reasons" tw={tw} />} />
          <ClientsQuotedHero kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
          <AvgValueHero kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
        </div>
        <div className="mt-4">
          <NewClients
            data={kpis.new_clients}
            doors={kpis.unique_clients?.new_names}
            footer={<Takeaways tKey="new_clients" tw={tw} />}
          />
        </div>
      </section>

      {/* 02 ------------------------------------------------ trending */}
      <section>
        <SectionHeader
          n="02"
          title="How are we trending?"
          sub="The direction of travel: how this quarter's quotes are converting, how fast work moves through the pipeline, and how the mix of work is shifting."
        />
        <div className="grid grid-cols-1 gap-4">
          <WonHero kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
        </div>
        <div className="mt-4">
          <SpeedJourney kpis={kpis} editable={editable} onOverride={onOverride} tw={tw} />
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4">
          <WorkMix data={kpis.work_mix} cmp={kpis.compare?.yoy} footer={<Takeaways tKey="work_mix" tw={tw} />} />
        </div>
      </section>

      {/* 03 ------------------------------------------------ lifecycle */}
      <section>
        <SectionHeader
          n="03"
          title="How busy have we been - and will be?"
          sub="The project lifecycle this quarter: how much work hit each milestone, team by team, and how fast projects move between the steps."
        />
        <QuarterLifecycle
          data={kpis.lifecycle}
          compare={kpis.compare}
          now={{
            in_field_now: kpis.in_field_now,
            awaiting_contracts: kpis.awaiting_contracts,
            about_to_start: kpis.about_to_start,
          }}
          footer={<Takeaways tKey="lifecycle" tw={tw} />}
        />
      </section>

      {/* 04 ------------------------------------------------ records & culture */}
      <section>
        <SectionHeader
          n="04"
          title="Records & culture"
          sub="The things worth celebrating: happy clients, long relationships and the records we set."
        />
        <Milestones data={kpis.milestones} records={kpis.records} footer={<Takeaways tKey="milestones" tw={tw} />} />
        <div className="grid grid-cols-1 gap-4 mt-4">
          <RecommendWaffle
            rec={kpis.recommend}
            editable={editable}
            onOverride={onOverride}
            footer={<Takeaways tKey="recommend" tw={tw} />}
          />
        </div>
        <div className="mt-4">
          <TopicsCloud topics={kpis.topics} footer={<Takeaways tKey="topics" tw={tw} />} />
        </div>
      </section>
    </div>
    </CmpContext.Provider>
    </InfoTipContext.Provider>
    </TooltipProvider>
  );
}
